import React, { useState, useEffect } from 'react';
import { config } from './config';

// --- アイコンコンポーネント (維持) ---
const PlusIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="12" y1="5" x2="12" y2="19"></line><line x1="5" y1="12" x2="19" y2="12"></line></svg>;
const EditIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path></svg>;
const TrashIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"></polyline><path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path><line x1="10" y1="11" x2="10" y2="17"></line><line x1="14" y1="11" x2="14" y2="17"></line></svg>;
const EyeIcon = () => <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M2 12s3-7 10-7 10 7 10 7-3 7-10 7-10-7-10-7Z"></path><circle cx="12" cy="12" r="3"></circle></svg>;
const CalendarIcon = () => (<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect width="18" height="18" x="3" y="4" rx="2" ry="2" /><line x1="16" x2="16" y1="2" y2="6" /><line x1="8" x2="8" y1="2" y2="6" /><line x1="3" x2="21" y1="10" y2="10" /></svg>);

// --- カラー定義 ---
const SECTION_COLORS = {
  blue: "bg-blue-50 border-blue-200",
  green: "bg-green-50 border-green-200",
  yellow: "bg-yellow-50 border-yellow-200",
  red: "bg-red-50 border-red-200",
  purple: "bg-purple-50 border-purple-200",
  orange: "bg-orange-50 border-orange-200",
  gray: "bg-gray-50 border-gray-200"
};

const BORDER_PRESETS = {
  "": "指定なし",
  "#3b82f6": "ブルー",
  "#10b981": "グリーン",
  "#ef4444": "レッド",
  "#f59e0b": "イエロー",
  "#8b5cf6": "パープル",
  "#000000": "ブラック"
};

const COLOR_NAMES = {
  blue: "ブルー", green: "グリーン", yellow: "イエロー", red: "レッド", purple: "パープル", orange: "オレンジ", gray: "グレー"
};

// --- ヘルパー関数 ---
const formatTimestamp = (isoString) => {
  if (!isoString) return "";
  const date = new Date(isoString);
  return `${date.getFullYear()}年/${(date.getMonth() + 1).toString().padStart(2, '0')}月/${date.getDate().toString().padStart(2, '0')}日 ${date.getHours().toString().padStart(2, '0')}時${date.getMinutes().toString().padStart(2, '0')}分`;
};

const groupFieldsBySections = (fields) => {
  const groups = [];
  let currentGroup = { isHeader: false, label: "共通項目", color: "gray", fields: [] };
  (fields || []).forEach(f => {
    if (f.type === 'section') {
      if (currentGroup.fields.length > 0 || currentGroup.label !== "共通項目") { groups.push(currentGroup); }
      currentGroup = { isHeader: true, label: f.label, color: f.sectionColor || 'gray', fields: [] };
    } else { currentGroup.fields.push(f); }
  });
  groups.push(currentGroup);
  return groups;
};

const api = {
  getFormConfig: async () => {
    const response = await fetch(`${config.api.invokeUrl}/form-config`);
    if (!response.ok) return { fields: [] };
    return await response.json();
  },
  saveFormConfig: async (fields) => {
    await fetch(`${config.api.invokeUrl}/form-config`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ fields }),
    });
  },
  getReports: async () => {
    const r = await fetch(`${config.api.invokeUrl}/reports`);
    return await r.json();
  },
  getReport: async (tid, dt) => {
    const r = await fetch(`${config.api.invokeUrl}/reports/${tid}/${dt}`);
    return await r.json();
  },
  createReport: async (d) => {
    const r = await fetch(`${config.api.invokeUrl}/reports`, {
      method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(d)
    });
    return await r.json();
  },
  updateReport: async (tid, dt, d) => {
    const r = await fetch(`${config.api.invokeUrl}/reports/${tid}/${dt}`, {
      method: 'PUT', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(d)
    });
    return await r.json();
  },
  deleteReport: async (tid, dt) => {
    await fetch(`${config.api.invokeUrl}/reports/${tid}/${dt}`, { method: 'DELETE' });
  }
};

const Header = ({ title, children }) => (
  <header className="bg-white shadow-md sticky top-0 z-10">
    <div className="max-w-4xl mx-auto py-4 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
      <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
      <div>{children}</div>
    </div>
  </header>
);

// --- 項目編集モーダル ---
const ItemEditModal = ({ item, onSave, onCancel }) => {
  const [editedItem, setEditedItem] = useState(JSON.parse(JSON.stringify(item || {
    type: 'text', sectionColor: 'blue', width: '100', requiredForSameDay: false, borderColor: ''
  })));

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setEditedItem(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex justify-center items-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-2xl space-y-4 max-h-full overflow-y-auto">
        <h2 className="text-xl font-black text-gray-800 border-b pb-2">{item ? '項目の編集' : '新規項目の追加'}</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div><label className="block text-sm font-bold text-gray-700">タイプ</label>
            <select name="type" value={editedItem.type} onChange={handleChange} className="mt-1 block w-full p-2 border-2 border-gray-200 rounded-lg">
              <option value="section">セクション分割</option>
              <option value="text">テキスト</option><option value="textarea">複数行</option>
              <option value="number">数値</option><option value="date">日付</option>
              <option value="radio">単一選択</option><option value="select">リスト選択</option>
              <option value="staff_selector">場内員セレクター</option><option value="divider">区切り線</option>
            </select>
          </div>
          <div><label className="block text-sm font-bold text-gray-700">ラベル / セクション名</label>
            <input name="label" value={editedItem.label || ''} onChange={handleChange} className="mt-1 block w-full p-2 border-2 border-gray-200 rounded-lg" />
          </div>
          {editedItem.type === 'section' ? (
            <div><label className="block text-sm font-bold text-gray-700">セクションの色</label>
              <select name="sectionColor" value={editedItem.sectionColor || 'blue'} onChange={handleChange} className="mt-1 block w-full p-2 border-2 border-gray-200 rounded-lg">
                {Object.entries(COLOR_NAMES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
          ) : (
            <>
              <div><label className="block text-sm font-bold text-gray-700">項目ID (英数字)</label>
                <input name="id" value={editedItem.id || ''} onChange={handleChange} readOnly={!!item} className={`mt-1 block w-full p-2 border-2 border-gray-200 rounded-lg ${item ? 'bg-gray-100' : ''}`} />
              </div>
              <div className="col-span-1 md:col-span-2 p-3 bg-gray-50 rounded-xl border-2 border-dashed">
                <p className="text-xs font-bold text-gray-500 mb-2 tracking-widest uppercase">枠線の色（border）の設定</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-[10px] font-bold text-gray-400">プリセット</label>
                    <select name="borderColor" value={Object.keys(BORDER_PRESETS).includes(editedItem.borderColor) ? editedItem.borderColor : 'custom'} onChange={handleChange} className="mt-1 block w-full p-2 border-2 border-gray-200 rounded-lg text-sm">
                      {Object.entries(BORDER_PRESETS).map(([code, name]) => <option key={code} value={code}>{name}</option>)}
                      <option value="custom">カラーコードで指定...</option>
                    </select>
                  </div>
                  {(!Object.keys(BORDER_PRESETS).includes(editedItem.borderColor) || editedItem.borderColor === 'custom') && (
                    <div>
                      <label className="block text-[10px] font-bold text-gray-400">カラーコード（例: #FF0000）</label>
                      <input name="borderColor" value={editedItem.borderColor === 'custom' ? '' : editedItem.borderColor} onChange={handleChange} className="mt-1 block w-full p-2 border-2 border-gray-200 rounded-lg text-sm" placeholder="#000000" />
                    </div>
                  )}
                </div>
              </div>
              <div><label className="block text-sm font-bold text-gray-700">表示幅</label>
                <select name="width" value={editedItem.width || '100'} onChange={handleChange} className="mt-1 block w-full p-2 border-2 border-gray-200 rounded-lg">
                  <option value="100">100%</option><option value="50">50%</option>
                </select>
              </div>
              {editedItem.type === 'number' && (
                <div><label className="block text-sm font-bold text-gray-700">単位</label>
                  <input name="unit" value={editedItem.unit || ''} onChange={handleChange} className="mt-1 block w-full p-2 border-2 border-gray-200 rounded-lg" placeholder="kg, h など" />
                </div>
              )}
            </>
          )}
        </div>
        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button onClick={onCancel} className="px-6 py-2 border-2 border-gray-200 rounded-full font-bold text-gray-500">キャンセル</button>
          <button onClick={() => onSave(editedItem)} className="px-8 py-2 bg-blue-600 text-white rounded-full font-black shadow-lg">保存</button>
        </div>
      </div>
    </div>
  );
};

// --- 管理者画面 ---
const AdminView = ({ setView }) => {
  const [fields, setFields] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    api.getFormConfig().then(d => { setFields(d.fields || []); setIsLoading(false); });
  }, []);

  const moveItem = (idx, dir) => {
    const newFields = [...fields];
    const targetIdx = idx + dir;
    if (targetIdx < 0 || targetIdx >= fields.length) return;
    [newFields[idx], newFields[targetIdx]] = [newFields[targetIdx], newFields[idx]];
    setFields(newFields);
  };

  if (isLoading) return <div className="p-20 text-center font-black text-gray-300">LOADING...</div>;

  return (
    <div className="min-h-screen bg-gray-50">
      <Header title="項目・順番設定">
        <div className="space-x-2">
          <button onClick={() => { setEditingItem(null); setIsModalOpen(true); }} className="bg-blue-600 text-white px-4 py-2 rounded-full font-bold shadow-md">+ 追加</button>
          <button onClick={() => api.saveFormConfig(fields).then(() => alert('保存完了'))} className="bg-indigo-600 text-white px-6 py-2 rounded-full font-black shadow-md">設定を保存</button>
        </div>
      </Header>
      <main className="max-w-4xl mx-auto py-8 px-4">
        <div className="space-y-2">
          {fields.map((f, i) => (
            <div key={f.id || i} className={`p-4 flex items-center justify-between rounded-xl border-2 transition-all ${f.type === 'section' ? 'bg-gray-800 border-gray-700 text-white' : 'bg-white border-gray-100 text-gray-800 shadow-sm'}`}>
              <div className="flex items-center space-x-4">
                <div className="flex flex-col space-y-1">
                  <button onClick={() => moveItem(i, -1)} disabled={i === 0} className="hover:text-blue-400 disabled:opacity-20 text-xs">▲</button>
                  <button onClick={() => moveItem(i, 1)} disabled={i === fields.length - 1} className="hover:text-blue-400 disabled:opacity-20 text-xs">▼</button>
                </div>
                <div>
                  <span className={`text-xs font-black px-2 py-0.5 rounded-md mr-2 ${f.type === 'section' ? 'bg-blue-500 text-white' : 'bg-gray-200 text-gray-600'}`}>
                    {f.type.toUpperCase()}
                  </span>
                  <span className="font-black">{f.label}</span>
                  {!f.type === 'section' && f.borderColor && (
                    <span className="ml-2 w-3 h-3 rounded-full inline-block border" style={{ backgroundColor: f.borderColor }}></span>
                  )}
                </div>
              </div>
              <div className="flex space-x-2">
                <button onClick={() => { setEditingItem(f); setIsModalOpen(true); }} className="p-2 hover:bg-gray-100 rounded-full transition-colors"><EditIcon /></button>
                <button onClick={() => setFields(fields.filter((_, idx) => idx !== i))} className="p-2 hover:bg-red-50 text-red-400 rounded-full transition-colors"><TrashIcon /></button>
              </div>
            </div>
          ))}
        </div>
        <div className="mt-10 text-center"><button onClick={() => setView('report_list')} className="text-gray-400 font-bold hover:underline">一覧画面へ戻る</button></div>
      </main>
      {isModalOpen && <ItemEditModal item={editingItem} onSave={(item) => {
        const idx = fields.findIndex(f => f.id === item.id && f.id !== undefined);
        if (idx > -1 && item.id) { const n = [...fields]; n[idx] = item; setFields(n); }
        else { setFields([...fields, { ...item, id: item.type === 'section' ? `sec_${Date.now()}` : (item.id || `field_${Date.now()}`) }]); }
        setIsModalOpen(false);
      }} onCancel={() => setIsModalOpen(false)} />}
    </div>
  );
};

// --- 動的フィールド ---
const DynamicField = ({ field, value, onChange, disabled }) => {
  const { id, label, type, unit, options, borderColor } = field;
  const customBorderStyle = borderColor ? { borderColor: borderColor, borderWidth: '2px' } : {};

  const handleChange = (e) => !disabled && onChange(id, e.target.value);
  const handleStaffChange = (t, m) => {
    const cur = value || { shain: { members: [] }, part: { members: [] } };
    const curM = cur[t].members;
    const newM = curM.includes(m) ? curM.filter(x => x !== m) : [...curM, m];
    onChange(id, { ...cur, [t]: { ...cur[t], members: newM, count: newM.length } });
  };

  if (type === 'divider') return <div className="md:col-span-2 border-t pt-4 mt-2"><h4 className="text-gray-400 font-bold text-sm tracking-widest">{label}</h4></div>;

  return (
    <div className={field.width === '50' ? 'md:col-span-1' : 'md:col-span-2'}>
      <label className="block text-sm font-black text-gray-700 mb-2">{label}</label>
      <div className="mt-1">
        {type === 'staff_selector' ? (
          <div className="flex flex-col space-y-4 p-4 border-2 rounded-xl bg-white bg-opacity-50" style={customBorderStyle}>
            {['shain', 'part'].map(key => (
              <div key={key}>
                <p className="font-bold text-[10px] text-gray-400 mb-2 uppercase tracking-tighter">{key === 'shain' ? '社員' : 'パート'} - {value?.[key]?.count || 0}名</p>
                <div className="flex flex-wrap gap-2">
                  {(options?.[key] || []).map(n => (
                    <label key={n} className={`flex items-center space-x-1.5 text-sm p-1.5 px-3 rounded-lg border-2 transition-all ${value?.[key]?.members?.includes(n) ? 'bg-blue-600 text-white border-blue-700 shadow-md' : 'bg-white text-gray-600 border-gray-100'}`}>
                      <input type="checkbox" className="hidden" checked={value?.[key]?.members?.includes(n)} onChange={() => handleStaffChange(key, n)} disabled={disabled} />
                      <span>{n}</span>
                    </label>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ) : type === 'number' ? (
          <div className="relative flex items-center group">
            <input type="number" value={value || ''} onChange={handleChange} disabled={disabled} style={customBorderStyle} className="w-full border-2 border-gray-200 p-2.5 rounded-xl focus:border-blue-500 focus:ring-0 transition-all pr-16 font-bold" />
            {unit && <span className="absolute right-3 px-2 py-1 bg-gray-100 text-gray-500 text-[10px] font-black rounded-md border border-gray-200 uppercase">{unit}</span>}
          </div>
        ) : type === 'textarea' ? (<textarea value={value || ''} onChange={handleChange} disabled={disabled} style={customBorderStyle} rows="3" className="w-full border-2 border-gray-200 p-2.5 rounded-xl focus:border-blue-500 focus:ring-0 transition-all font-bold" />
        ) : (<input type={type === 'date' ? 'date' : 'text'} value={value || ''} onChange={handleChange} disabled={disabled} style={customBorderStyle} className="w-full border-2 border-gray-200 p-2.5 rounded-xl focus:border-blue-500 focus:ring-0 transition-all font-bold" />)}
      </div>
    </div>
  );
};

// --- 日報一覧 ---
const ReportListView = ({ setView, onEditReport }) => {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  useEffect(() => {
    api.getReports().then(d => { setReports(Array.isArray(d) ? d.sort((a, b) => new Date(b['日付']) - new Date(a['日付'])) : []); setIsLoading(false); });
  }, []);
  const groupReports = (data) => {
    return data.reduce((acc, r) => {
      const d = new Date(r['日付']); const y = d.getFullYear(); const m = (d.getMonth() + 1).toString().padStart(2, '0');
      if (!acc[y]) acc[y] = {}; if (!acc[y][m]) acc[y][m] = [];
      acc[y][m].push(r); return acc;
    }, {});
  };
  if (isLoading) return <div className="p-20 text-center text-gray-300 font-black">FETCHING...</div>;
  const grouped = groupReports(reports);
  return (
    <div className="bg-white min-h-screen">
      <Header title="日報一覧">
        <div className="flex space-x-3">
          <button onClick={() => setView('admin')} className="text-gray-400 font-bold hover:text-gray-600 transition-colors">設定</button>
          <button onClick={() => setView('user_create')} className="bg-gray-900 text-white px-6 py-2 rounded-full font-black shadow-xl transform hover:scale-105 transition-all">+ 作成</button>
        </div>
      </Header>
      <main className="max-w-4xl mx-auto py-8 px-4">
        {Object.keys(grouped).sort((a, b) => b - a).map(y => (
          <div key={y} className="mb-10">
            <h2 className="text-3xl font-black mb-4">{y}</h2>
            {Object.keys(grouped[y]).sort((a, b) => b - a).map(m => (
              <div key={m} className="mb-6 ml-2 border-l-4 border-black-100 pl-4">
                <h3 className="font-black text-gray-400 mb-4">{m}月</h3>
                <div className="grid gap-4">
                  {grouped[y][m].map(r => (
                    <div key={r['チームID']} className="p-5 flex flex-col sm:flex-row justify-between items-start sm:items-center bg-white rounded-2xl border-2 border-gray-50 hover:border-blue-100 hover:shadow-xl transition-all">
                      <div>
                        <p className="font-black text-xl text-gray-800">{r['日付']}</p>
                        <div className="flex flex-wrap gap-2 mt-2">
                          <span className={`px-2 py-0.5 text-[10px] rounded-md font-black uppercase ${r.isConfirmed ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                            {r.isConfirmed ? '承認済み' : '未承認'}
                          </span>
                          {r.isConfirmed && <span className="text-[10px] text-gray-300 font-mono self-center">{formatTimestamp(r.confirmedAt)}</span>}
                        </div>
                        {r.isConfirmed && r.approvalComment && (
                          <div className="mt-3 text-sm text-gray-600 bg-gray-50 p-3 rounded-xl border border-gray-100 italic">“{r.approvalComment}”</div>
                        )}
                      </div>
                      <div className="flex space-x-2 mt-4 sm:mt-0">
                        <a href={r.htmlUrl} target="_blank" rel="noopener noreferrer" className="p-3 bg-gray-50 rounded-full text-gray-400 hover:text-blue-600 transition-all"><EyeIcon /></a>
                        <button onClick={() => onEditReport(r['チームID'], r['日付'])} className="p-3 bg-gray-50 rounded-full text-gray-400 hover:text-indigo-600 transition-all"><EditIcon /></button>
                        <button onClick={() => { if (window.confirm('削除しますか？')) api.deleteReport(r['チームID'], r['日付']).then(() => window.location.reload()) }} className="p-3 bg-gray-50 rounded-full text-gray-400 hover:text-red-400 transition-all"><TrashIcon /></button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        ))}
      </main>
    </div>
  );
};

// --- 利用者画面 ---
const UserView = ({ setView, reportToEdit, isConfirmMode = false }) => {
  const [fields, setFields] = useState([]);
  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [approvalComment, setApprovalComment] = useState("");

  useEffect(() => {
    const load = async () => {
      const c = await api.getFormConfig(); const f = c.fields || []; setFields(f);
      if (!!reportToEdit) {
        const d = await api.getReport(reportToEdit.teamId, reportToEdit.date);
        setFormData(d); setApprovalComment(d.approvalComment || "");
      } else {
        const init = {}; f.forEach(x => { if (x.type === 'date') init[x.id] = new Date().toISOString().slice(0, 10); });
        setFormData(init);
      }
      setIsLoading(false);
    };
    load();
  }, [reportToEdit]);

  const grouped = groupFieldsBySections(fields);

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      <Header title={isConfirmMode ? "日報承認" : (!!reportToEdit ? "修正" : "日報作成")} />
      <main className="max-w-4xl mx-auto py-8 px-4">
        <form onSubmit={async (e) => {
          e.preventDefault();
          try {
            if (!!reportToEdit) await api.updateReport(reportToEdit.teamId, reportToEdit.date, formData);
            else await api.createReport(formData);
            alert('送信完了'); setView('report_list');
          } catch (e) { alert('通信エラー'); }
        }} className="space-y-10">
          {grouped.map((group, gIdx) => (
            <div key={gIdx} className={`p-8 rounded-3xl shadow-xl border-t-8 bg-white transition-all ${SECTION_COLORS[group.color]}`}>
              {group.label && <h3 className="text-xl font-black mb-8 border-b-2 border-white pb-2 text-gray-800">{group.label}</h3>}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                {group.fields.map(f => (
                  <DynamicField key={f.id} field={f} value={formData[f.id]} onChange={(id, v) => setFormData(p => ({ ...p, [id]: v }))} disabled={isConfirmMode} />
                ))}
              </div>
            </div>
          ))}
          {isConfirmMode && (
            <div className="mt-12 p-8 bg-white rounded-3xl shadow-lg border-2 border-gray-100">
              <label className="block text-sm font-black text-gray-400 mb-4 tracking-tighter uppercase">管理者コメント</label>
              <textarea value={approvalComment} onChange={(e) => setApprovalComment(e.target.value)} placeholder="指示事項やフィードバックがあれば入力してください" rows="3" className="w-full bg-gray-50 border-2 border-gray-100 p-4 rounded-2xl text-gray-800 font-bold focus:bg-white transition-all" />
            </div>
          )}
          <div className="flex justify-between items-center mt-16 p-8 bg-white rounded-3xl shadow-2xl">
            <button type="button" onClick={() => setView('report_list')} className="text-gray-300 font-black hover:text-gray-500 transition-colors">BACK</button>
            {isConfirmMode ? (
              <button type="button" onClick={async () => {
                const d = { ...formData, isConfirmed: true, confirmedAt: new Date().toISOString(), approvalComment };
                await api.updateReport(reportToEdit.teamId, reportToEdit.date, d);
                alert('承認完了'); window.location.href = window.location.origin;
              }} className="bg-green-500 text-white px-16 py-4 rounded-full font-black shadow-xl hover:bg-green-600 transform hover:scale-105 transition-all">日報を承認する</button>
            ) : (
              <button type="submit" className="bg-blue-600 text-white px-16 py-4 rounded-full font-black shadow-xl hover:bg-blue-700 transform hover:scale-105 transition-all">{!!reportToEdit ? "修正内容を保存" : "日報を送信"}</button>
            )}
          </div>
        </form>
      </main>
    </div>
  );
};

export default function App() {
  const [view, setView] = useState('report_list');
  const [reportToEdit, setReportToEdit] = useState(null);
  useEffect(() => {
    const p = new URLSearchParams(window.location.search);
    if (p.get('mode') === 'confirm' && p.get('teamId') && p.get('date')) {
      setReportToEdit({ teamId: p.get('teamId'), date: p.get('date') }); setView('user_confirm');
    }
  }, []);
  const render = () => {
    switch (view) {
      case 'admin': return <AdminView setView={setView} />;
      case 'user_create': return <UserView setView={setView} reportToEdit={null} />;
      case 'user_edit': return <UserView setView={setView} reportToEdit={reportToEdit} />;
      case 'user_confirm': return <UserView setView={setView} reportToEdit={reportToEdit} isConfirmMode={true} />;
      default: return <ReportListView setView={setView} onEditReport={(t, d) => { setReportToEdit({ teamId: t, date: d }); setView('user_edit'); }} />;
    }
  };
  return <div className="selection:bg-blue-200">{render()}</div>;
}